"use client";

import { useEffect, useState } from "react";
import type { UserTagSuggestion } from "@/components/posts/user-tag-picker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  type Participant,
  suggestionToParticipant,
  TeamCard,
  ValidationWarning,
} from "./base-match-template";
import { ActivityCourtPreview, ActivityDisplayPreview } from "./previews";
import { TennisScoreEditor } from "./score-editors/tennis-score-editor";

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export type TennisMode = "singles" | "doubles";

export type TennisSetScore = {
  yourScore: number;
  opponentScore: number;
};

export type TennisTemplateData = {
  mode: TennisMode;
  matchScore: { yourSets: number; opponentSets: number };
  sets: TennisSetScore[];
  positions: Record<string, "your-team" | "opponent-team">;
  hasScore?: boolean;
};

export type TennisParticipant = Participant;

// ──────────────────────────────────────────────────────────────────────────────
// COMPONENT PROPS
// ──────────────────────────────────────────────────────────────────────────────

type TennisTemplateProps = {
  currentUser: TennisParticipant;
  onChange: (
    data: TennisTemplateData,
    participants: TennisParticipant[],
  ) => void;
  initialData?: Partial<TennisTemplateData>;
};

// ──────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

export function TennisTemplate({
  currentUser,
  onChange,
  initialData,
}: TennisTemplateProps) {
  const [mode, setMode] = useState<TennisMode>(initialData?.mode ?? "singles");
  const [participants, setParticipants] = useState<TennisParticipant[]>([
    currentUser,
  ]);
  const [teams, setTeams] = useState<
    Record<string, "your-team" | "opponent-team">
  >(() => ({
    [currentUser.id]: "your-team",
    ...(initialData?.positions ?? {}),
  }));

  type TennisScorePart = Pick<TennisTemplateData, "matchScore" | "sets">;
  const [scorePart, setScorePart] = useState<TennisScorePart>(() => ({
    matchScore: initialData?.matchScore ?? { yourSets: 0, opponentSets: 0 },
    sets: initialData?.sets ?? [],
  }));

  const maxParticipants = mode === "singles" ? 2 : 4;
  const yourTeamMax = mode === "singles" ? 1 : 2;
  const opponentTeamMax = mode === "singles" ? 1 : 2;

  const yourTeam = participants.filter((p) => teams[p.id] === "your-team");
  const opponentTeam = participants.filter(
    (p) => teams[p.id] === "opponent-team",
  );
  const selectedUserIds = new Set(participants.map((p) => p.id));

  // Update parent whenever state changes
  useEffect(() => {
    const data: TennisTemplateData = {
      mode,
      matchScore: scorePart.matchScore,
      sets: scorePart.sets ?? [],
      positions: teams,
      hasScore: true, // Default to true for new posts
    };
    onChange(data, participants);
  }, [mode, scorePart, teams, participants, onChange]);

  // Handle mode change
  const handleModeChange = (newMode: TennisMode) => {
    setMode(newMode);
    // Keep current user, reset others if switching to singles with too many
    if (newMode === "singles") {
      const yourTeamFiltered = yourTeam.slice(0, 1);
      const opponentFiltered = opponentTeam.slice(0, 1);
      const newParticipants = [...yourTeamFiltered, ...opponentFiltered];
      if (!newParticipants.find((p) => p.id === currentUser.id)) {
        newParticipants.unshift(currentUser);
      }
      setParticipants(newParticipants);

      const newTeams: Record<string, "your-team" | "opponent-team"> = {
        [currentUser.id]: "your-team",
      };
      for (const p of opponentFiltered) {
        newTeams[p.id] = "opponent-team";
      }
      setTeams(newTeams);
    }
  };

  // Add participant to a team
  const handleAddParticipant = (
    suggestion: UserTagSuggestion,
    team: "your-team" | "opponent-team",
  ) => {
    if (participants.length >= maxParticipants) return;

    const teamCount =
      team === "your-team" ? yourTeam.length : opponentTeam.length;
    const teamMax = team === "your-team" ? yourTeamMax : opponentTeamMax;
    if (teamCount >= teamMax) return;

    const newParticipant = suggestionToParticipant(suggestion);
    setParticipants((prev) => [...prev, newParticipant]);
    setTeams((prev) => ({ ...prev, [suggestion.id]: team }));
  };

  // Remove participant
  const handleRemoveParticipant = (userId: string) => {
    if (userId === currentUser.id) return;
    setParticipants((prev) => prev.filter((p) => p.id !== userId));
    setTeams((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const yourSets = scorePart.matchScore.yourSets;
  const opponentSets = scorePart.matchScore.opponentSets;
  const yourWon = yourSets > opponentSets;
  const opponentWon = opponentSets > yourSets;
  const isDraw = yourSets === opponentSets && yourSets > 0;

  // Validation
  const isYourTeamComplete = yourTeam.length === yourTeamMax;
  const isOpponentComplete = opponentTeam.length === opponentTeamMax;
  const isComplete = isYourTeamComplete && isOpponentComplete;

  const team1Label = "Team 1";
  const team2Label = "Team 2";

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <div className="space-y-2">
        <Label className="text-text-primary">Match Type</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={mode === "singles" ? "default" : "outline"}
            size="sm"
            onClick={() => handleModeChange("singles")}
            className={cn(mode === "singles" && "bg-brand text-text-inverse")}
          >
            Singles
          </Button>
          <Button
            type="button"
            variant={mode === "doubles" ? "default" : "outline"}
            size="sm"
            onClick={() => handleModeChange("doubles")}
            className={cn(mode === "doubles" && "bg-brand text-text-inverse")}
          >
            Doubles
          </Button>
        </div>
      </div>

      {/* Teams Display */}
      <div className="grid grid-cols-2 gap-3">
        {/* Team 1 */}
        <TeamCard
          label={team1Label}
          isYourTeam
          players={yourTeam}
          maxPlayers={yourTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "your-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={yourWon || isDraw}
          isComplete={isYourTeamComplete}
        />

        {/* Opponent Team (Team 2) */}
        <TeamCard
          label={team2Label}
          isYourTeam={false}
          players={opponentTeam}
          maxPlayers={opponentTeamMax}
          currentUserId={currentUser.id}
          selectedUserIds={selectedUserIds}
          onAddPlayer={(s) => handleAddParticipant(s, "opponent-team")}
          onRemovePlayer={handleRemoveParticipant}
          highlight={opponentWon}
          isComplete={isOpponentComplete}
        />
      </div>

      {/* Validation Warning */}
      {!isComplete && (
        <ValidationWarning
          message={
            !isYourTeamComplete && !isOpponentComplete
              ? "Add players to both sides to post"
              : !isOpponentComplete
                ? `Add ${opponentTeamMax - opponentTeam.length} more to Team 2`
                : "Add players to Team 1"
          }
        />
      )}

      <TennisScoreEditor
        initialData={initialData as unknown as Record<string, unknown>}
        onChange={(data) => setScorePart(data as unknown as TennisScorePart)}
      />

      {/* Court Preview */}
      {participants.length >= 2 && (
        <div className="space-y-2">
          <Label className="text-text-primary">Preview</Label>
          <ActivityCourtPreview
            activityId="tennis"
            mode={mode}
            yourTeam={yourTeam}
            opponentTeam={opponentTeam}
            yourWon={yourWon}
          />
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DISPLAY COMPONENT (for feed)
// ──────────────────────────────────────────────────────────────────────────────

type TennisDisplayProps = {
  data: TennisTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

export function TennisDisplay({ data, participants }: TennisDisplayProps) {
  return (
    <ActivityDisplayPreview
      activityId="tennis"
      data={data}
      participants={participants}
    />
  );
}
